import { plural } from '@/lib/plural';
import { isUnreviewed } from './feedbackBoard';
import type { FeedbackItem, FeedbackSyncSummary } from '@/lib/apiClient';

/**
 * How an item's status compares with its GitHub issue, as the Feedback tab shows it.
 *
 *   'none'      nothing to flag: no issue, or both sides agree
 *   'pending'   a change made here has not reached GitHub yet; the nightly sync retries it
 *   'mismatch'  GitHub still shows the issue in a different state, and nobody has decided which side is right. This is
 *               what every item closed in the Admin tab BEFORE the two were synced looks like, so it is shown with a
 *               button to settle it, never fixed automatically.
 */
export type FeedbackSyncState = 'none' | 'pending' | 'mismatch';

export function feedbackSyncState(
  i: Pick<FeedbackItem, 'githubIssueNumber' | 'status' | 'githubState' | 'githubPushPending'>,
): FeedbackSyncState {
  if (i.githubIssueNumber == null) return 'none';
  if (i.githubPushPending) return 'pending';
  if (i.githubState && i.githubState !== i.status) return 'mismatch';
  return 'none';
}

/**
 * Which items the Feedback tab lists. `'decide'` is everything flagged by `feedbackSyncState`; `'review'` is every item nobody has
 * reviewed for the shared board yet (`boardVisibility === 'unreviewed'`), open or closed.
 */
export type FeedbackFilter = 'open' | 'all' | 'decide' | 'review';

type Filterable = Pick<FeedbackItem, 'githubIssueNumber' | 'status' | 'githubState' | 'githubPushPending' | 'boardVisibility'>;

/**
 * ⚠️ THE FLAGGED ITEMS MUST BE REACHABLE FROM THE DEFAULT VIEW. The tab opens on "Open", and the item that needs a decision
 * is by definition CLOSED here (closed before the two were synced, still open on GitHub). A ⚠ that only shows on the
 * "All" filter is invisible exactly where the sync banner tells an admin to look, so the flagged items have a filter of
 * their own, with a count beside it.
 */
export function filterFeedback<T extends Filterable>(items: readonly T[], filter: FeedbackFilter): T[] {
  switch (filter) {
    case 'all': return [...items];
    case 'decide': return items.filter((i) => feedbackSyncState(i) !== 'none');
    case 'review': return items.filter(isUnreviewed);
    default: return items.filter((i) => i.status === 'open');
  }
}

export const outOfStepCount = (items: readonly Filterable[]) => filterFeedback(items, 'decide').length;

const sentence = (a: string) => `${a[0].toUpperCase()}${a.slice(1)}.`;

/** One plain sentence for what a sync run did, and whether it deserves attention. */
export function describeSync(s: FeedbackSyncSummary): { tone: 'ok' | 'warn' | 'error'; text: string } {
  if (!s.configured) {
    return { tone: 'warn', text: 'GitHub is not connected for this environment, so there was nothing to sync.' };
  }

  const did: string[] = [];
  if (s.closed) did.push(`${plural(s.closed, 'item', 'items')} closed to match GitHub`);
  if (s.reopened) did.push(`${plural(s.reopened, 'item', 'items')} reopened to match GitHub`);
  if (s.pushed) did.push(`${plural(s.pushed, 'change', 'changes')} now on GitHub`);

  let pushStuck: string | null = null;
  if (s.pushFailed) {
    const why = s.pushErrors?.length ? ` (${s.pushErrors.join('; ')})` : '';
    pushStuck = `${plural(s.pushFailed, 'change is', 'changes are')} still not on GitHub${why}`;
  }

  if (s.error) {
    // ⚠️ The pushes are step 1 of a sync and the issue listing is step 2, so a failed listing comes back WITH the push counts.
    // Reporting only the error hides changes that did reach GitHub, and `syncNeedsAttention` still moves the admin to Out of
    // step when one did not, with nothing on the banner to say why. "Nothing was changed from GitHub's side" is true here
    // because the items are only closed or reopened from the listing; it is left out if a response says otherwise.
    const text = [
      did.length ? `${did.join(', ')}.` : null,
      pushStuck ? sentence(pushStuck) : null,
      `Could not read the issues from GitHub (${s.error}).`,
      s.closed || s.reopened ? null : "Nothing was changed from GitHub's side.",
      'Try again in a few minutes.',
    ].filter(Boolean).join(' ');
    return { tone: 'error', text };
  }

  const attention: string[] = [];
  if (pushStuck) attention.push(pushStuck);
  if (s.conflicts.length) {
    attention.push(`${plural(s.conflicts.length, 'item is', 'items are')} closed in the Project Tracker but still open on GitHub, listed under Out of step for you to decide`);
  }
  // Only counted when the list was complete, so this is a real absence. One deleted issue is a fact worth stating, and
  // it stays deleted: warning about it after every sync would teach admins to ignore the banner. But when MOST of what was
  // checked is missing, something has changed (another repository, a removed label) and nothing was really synced, which
  // "nothing needed changing" would then misreport. A response with no `checked` is treated as the second case.
  const info: string[] = [];
  if (s.notOnGitHub) {
    const missing = `${plural(s.notOnGitHub, 'item', 'items')} could not be found on GitHub (the issue may have been deleted or moved)`;
    const mostly = s.checked ? s.notOnGitHub * 2 >= s.checked : true;
    (mostly ? attention : info).push(missing);
  }
  if (!s.complete) attention.push("GitHub's list was cut short, so some items were not checked");

  const text = [
    did.length ? `${did.join(', ')}.` : 'Nothing needed changing.',
    ...info.map(sentence),
    ...attention.map(sentence),
  ].join(' ');
  return { tone: attention.length ? 'warn' : 'ok', text };
}

/** Whether a finished sync should move the admin to the Out of step list: anything left that is theirs to look at. */
export const syncNeedsAttention = (s: FeedbackSyncSummary) => s.conflicts.length > 0 || s.pushFailed > 0;
